import { useEffect, useState } from 'react';
import { Button, ConfirmModal, Modal } from '@safetyhub/ui';
import type { ComposerWidget, WidgetPresetRef } from '@safetyhub/shared';
import { objectParticleOf } from '@safetyhub/shared';
import { adminApi, type WidgetPresetWithVersions } from '../../lib/api';

interface PresetInstanceVersionPanelProps {
  /** 선택 인스턴스 루트의 출처 메타(배치 프리셋 id + 배치 시점 버전). */
  presetRef: WidgetPresetRef;
  /**
   * 버전 적용 — placedSnapshot(배치 당시 버전 스냅샷, 로컬 수정 감지 기준) + chosenSnapshot(적용할
   * 버전 스냅샷) + chosenVersion을 넘긴다. 로컬 수정 감지·경고·교체는 EditorPage(rc presetSubtreeEquivalent
   * + clonePresetSubtreeForInsert)가 담당한다. placedSnapshot이 없으면(구버전 스냅샷 부재) undefined.
   */
  onApplyVersion: (
    placedSnapshot: ComposerWidget | undefined,
    chosenSnapshot: ComposerWidget,
    chosenVersion: number,
  ) => void;
  /**
   * 현재 인스턴스 서브트리를 새 버전으로 발행 — normalize(출처 메타 제거)+publish+presetRef 재스탬프+
   * 라이브러리 갱신은 EditorPage. changeNote 필수. 성공 시 true(모달 닫힘). 재스탬프로 presetRef.version이
   * 바뀌면 이 패널이 자동 재조회된다.
   */
  onPublishCurrentVersion?: (changeNote: string) => Promise<boolean>;
  /**
   * 현재 인스턴스가 배치 버전 스냅샷 대비 수정됐는지 판정(rc presetSubtreeEquivalent — EditorPage가
   * anchor 서브트리를 알아 여기서 closure로 공급). "새 버전으로 저장" 버튼 게이트에 사용 —
   * 미수정이면 비활성(차이 없는 버전 양산 방지). 미지정이면 게이트 없이 항상 활성(하위호환).
   */
  computeModified?: (placedSnapshot: ComposerWidget) => boolean;
  /** 이름/설명 수정 — PATCH + 라이브러리 갱신은 EditorPage. 성공 시 true(패널 표시 갱신). */
  onUpdateMeta?: (body: { name: string; description: string }) => Promise<boolean>;
  /** 프리셋 soft delete — DELETE + 라이브러리 갱신은 EditorPage. 성공 시 true(패널 detach 표시 전환). */
  onDeletePreset?: () => Promise<boolean>;
}

/**
 * 프리셋 인스턴스(presetRef 보유 위젯)의 버전 관리 블록 — 속성 패널 상단.
 *
 * "프리셋 'name' v{배치}으로 배치 · 최신 v{현재}" 표시 + 버전 select(변경 메모 부기) + [이 버전 적용].
 * 버전 선택 = 업데이트·롤백 통일(구버전 재적용이 곧 롤백). 원본이 삭제됐거나(404) 조회 실패면
 * "원본 삭제됨(연결 해제)"만 표시하고 버전 UI를 숨긴다(복사본이라 배치 자체는 무영향).
 */
export function PresetInstanceVersionPanel({
  presetRef,
  onApplyVersion,
  onPublishCurrentVersion,
  computeModified,
  onUpdateMeta,
  onDeletePreset,
}: PresetInstanceVersionPanelProps) {
  const [preset, setPreset] = useState<WidgetPresetWithVersions | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'detached'>('loading');
  const [selectedVersion, setSelectedVersion] = useState<number>(presetRef.version);
  // 메타 수정 성공 후 자체 재조회 트리거(presetRef 불변이라 useEffect가 안 걸림) — nonce로 강제.
  const [reloadNonce, setReloadNonce] = useState(0);

  // 관리 액션(발행/이름·설명 수정/삭제) 모달 상태 — 진행 중 busy로 중복 제출 차단.
  const [busy, setBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNote, setPublishNote] = useState('');
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaName, setMetaName] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setPreset(null);
    void (async () => {
      try {
        const res = await adminApi.getWidgetPreset(presetRef.presetId);
        if (cancelled) return;
        if (res.success && res.data && res.data.deletedAt === null) {
          setPreset(res.data);
          // 기본 선택 = 최신 버전(업데이트 유도) — 이미 최신에 배치돼 있으면 그대로.
          setSelectedVersion(res.data.currentVersion);
          setState('ready');
        } else {
          setState('detached');
        }
      } catch {
        // 404(원본 삭제/비가시) 등 — detach 표시(배치본은 복사라 무영향).
        if (!cancelled) setState('detached');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presetRef.presetId, presetRef.version, reloadNonce]);

  const submitPublish = async () => {
    const note = publishNote.trim();
    if (!note || busy || !onPublishCurrentVersion) return;
    setBusy(true);
    const ok = await onPublishCurrentVersion(note);
    setBusy(false);
    if (ok) {
      setPublishOpen(false);
      setPublishNote('');
      // presetRef.version 재스탬프로 useEffect 자동 재조회 — 별도 refetch 불필요.
    }
  };

  const submitMeta = async () => {
    const name = metaName.trim();
    if (!name || busy || !onUpdateMeta) return;
    setBusy(true);
    const ok = await onUpdateMeta({ name, description: metaDesc.trim() });
    setBusy(false);
    if (ok) {
      setMetaOpen(false);
      setReloadNonce((n) => n + 1); // 패널 표시 이름/설명 갱신.
    }
  };

  const submitDelete = async () => {
    if (busy || !onDeletePreset) return;
    setBusy(true);
    const ok = await onDeletePreset();
    setBusy(false);
    if (ok) {
      setDeleteOpen(false);
      setState('detached'); // 삭제 성공 = 원본 detach 표시로 전환.
    }
  };

  if (state === 'loading') {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        프리셋 정보를 불러오는 중…
      </div>
    );
  }

  if (state === 'detached' || !preset) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
        <span className="font-medium text-gray-600">원본 삭제됨(연결 해제)</span>
        <p className="mt-1 leading-snug text-gray-400">
          이 위젯의 원본 프리셋을 찾을 수 없습니다. 배치본은 복사본이라 그대로 사용할 수 있습니다.
        </p>
      </div>
    );
  }

  const isOutdated = presetRef.version < preset.currentVersion;
  const versionsDesc = [...preset.versions].sort((a, b) => b.version - a.version);
  const chosen = versionsDesc.find((v) => v.version === selectedVersion);
  const placed = versionsDesc.find((v) => v.version === presetRef.version);
  const applyDisabled = !chosen || selectedVersion === presetRef.version;
  // "새 버전으로 저장" 게이트 — 배치 버전 스냅샷과 현재 서브트리가 같으면 비활성(차이 없는 버전
  // 양산 방지). 배치 스냅샷 부재·판정 콜백 미공급이면 게이트 없이 활성(안전 기본값).
  const publishUnchanged =
    placed != null && computeModified != null && !computeModified(placed.subtree);
  const hasManageActions =
    !!onPublishCurrentVersion || !!onUpdateMeta || !!onDeletePreset;

  return (
    <>
    <div className="rounded border border-brand-200 bg-brand-50 px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-brand-800" title={preset.name}>
            프리셋 · {preset.name}
          </div>
          <div className="mt-0.5 text-brand-700">
            v{presetRef.version}으로 배치
            {isOutdated ? (
              <span className="ml-1 text-amber-700">· 최신 v{preset.currentVersion}</span>
            ) : (
              <span className="ml-1 text-brand-500">· 최신</span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <select
          value={selectedVersion}
          onChange={(e) => setSelectedVersion(Number(e.target.value))}
          className="min-w-0 flex-1 rounded border border-brand-300 bg-white px-1.5 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-200"
          title={chosen?.changeNote}
        >
          {versionsDesc.map((v) => (
            <option key={v.version} value={v.version} title={v.changeNote}>
              v{v.version}
              {v.version === presetRef.version ? ' (현재 배치)' : ''}
              {v.version === preset.currentVersion ? ' (최신)' : ''} — {v.changeNote}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          size="xs"
          className="shrink-0"
          disabled={applyDisabled}
          onClick={() => {
            if (chosen) onApplyVersion(placed?.subtree, chosen.subtree, chosen.version);
          }}
          title={
            applyDisabled ? '현재 배치된 버전입니다.' : `v${selectedVersion}을 이 위치에 적용`
          }
        >
          이 버전 적용
        </Button>
      </div>
      {chosen ? (
        <p className="mt-1 leading-snug text-brand-600" title={chosen.changeNote}>
          변경 메모: <span className="text-brand-500">{chosen.changeNote}</span>
        </p>
      ) : null}
      {hasManageActions ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-brand-200 pt-2">
          {onPublishCurrentVersion ? (
            <button
              type="button"
              disabled={publishUnchanged}
              onClick={() => {
                setPublishNote('');
                setPublishOpen(true);
              }}
              className="rounded border border-brand-300 bg-white px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-50"
              title={
                publishUnchanged
                  ? '배치된 버전과 차이가 없습니다 — 위젯을 수정하면 활성화됩니다.'
                  : '지금 캔버스의 이 위젯 상태를 프리셋 새 버전으로 저장합니다.'
              }
            >
              현재 상태를 새 버전으로 저장
            </button>
          ) : null}
          {onUpdateMeta ? (
            <button
              type="button"
              onClick={() => {
                setMetaName(preset.name);
                setMetaDesc(preset.description ?? '');
                setMetaOpen(true);
              }}
              className="rounded border border-brand-300 bg-white px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100"
            >
              이름/설명 수정
            </button>
          ) : null}
          {onDeletePreset ? (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              프리셋 삭제
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
    {onPublishCurrentVersion ? (
      <Modal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title="현재 상태를 새 버전으로 저장"
        size="md"
      >
        <div className="space-y-3">
          <p className="rounded bg-brand-50 px-3 py-2 text-xs leading-relaxed text-brand-700">
            지금 캔버스의 이 위젯 상태가 프리셋 <b>{preset.name}</b>의 새 버전(v
            {preset.currentVersion + 1})으로 저장됩니다. 이미 배치된 다른 인스턴스는 자동으로 바뀌지
            않고, 각자 원할 때 버전을 적용합니다.
          </p>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="preset-publish-note-input"
            >
              변경 메모
            </label>
            <input
              id="preset-publish-note-input"
              type="text"
              value={publishNote}
              onChange={(e) => setPublishNote(e.target.value)}
              autoFocus
              placeholder="예: 라벨 문구 수정"
              className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
            <p className="mt-1 text-xs text-gray-400">
              버전 선택 시 판단 근거로 표시됩니다(필수).
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPublishOpen(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => void submitPublish()}
            disabled={!publishNote.trim() || busy}
          >
            저장
          </Button>
        </div>
      </Modal>
    ) : null}
    {onUpdateMeta ? (
      <Modal
        open={metaOpen}
        onClose={() => setMetaOpen(false)}
        title="프리셋 이름/설명 수정"
        size="md"
      >
        <div className="space-y-3">
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="preset-meta-name-input"
            >
              이름
            </label>
            <input
              id="preset-meta-name-input"
              type="text"
              value={metaName}
              onChange={(e) => setMetaName(e.target.value)}
              autoFocus
              className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
          </div>
          <div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="preset-meta-desc-input"
            >
              설명 <span className="font-normal text-gray-400">(선택)</span>
            </label>
            <input
              id="preset-meta-desc-input"
              type="text"
              value={metaDesc}
              onChange={(e) => setMetaDesc(e.target.value)}
              placeholder="언제 쓰는 프리셋인지 한 줄 설명"
              className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setMetaOpen(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => void submitMeta()}
            disabled={!metaName.trim() || busy}
          >
            저장
          </Button>
        </div>
      </Modal>
    ) : null}
    {onDeletePreset ? (
      <ConfirmModal
        open={deleteOpen}
        title="프리셋 삭제"
        body={`프리셋 "${preset.name}"${objectParticleOf(preset.name)} 삭제할까요? 이미 배치된 인스턴스는 복사본이라 그대로 유지되고, 원본이 사라져 "원본 삭제됨"으로 표시됩니다.`}
        confirmLabel="삭제"
        danger
        busy={busy}
        onConfirm={() => void submitDelete()}
        onClose={() => setDeleteOpen(false)}
      />
    ) : null}
    </>
  );
}
